import { useState, useEffect } from 'react';

export interface LandpageSettings {
  phone: string;
  address: string;
  headline: string;
  subheadline: string;
  showcase_title: string;
  supportEmail: string;
}

const DEFAULT_SETTINGS: LandpageSettings = {
  phone: '0806 475 2074',
  address: '2 Abiriba St, Uzuokoli Road, Umuahia, 440233, Abia State',
  headline: 'Escape Generator Stress.',
  subheadline: 'Power 24/7 With Clean Solar.',
  showcase_title: 'Access Tech Solar Engineering Showcase',
  supportEmail: 'support@accesstechnologiesandrenewableenergy.online',
};

export function useLandpageSettings() {
  const [settings, setSettingsState] = useState<LandpageSettings>(() => {
    try {
      const stored = localStorage.getItem('access_tech_landpage_settings');
      if (stored) {
        return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) };
      }
    } catch (e) {
      console.error('Error reading localStorage settings', e);
    }
    return DEFAULT_SETTINGS;
  });

  const updateSettings = (newSettings: Partial<LandpageSettings>) => {
    setSettingsState((prev) => {
      const updated = { ...prev, ...newSettings };
      try {
        localStorage.setItem('access_tech_landpage_settings', JSON.stringify(updated));
        // Dispatch custom event to sync state across different component instances
        window.dispatchEvent(new Event('landpage_settings_changed'));
      } catch (e) {
        console.error('Error saving to localStorage', e);
      }
      return updated;
    });
  };

  const resetToDefault = () => {
    setSettingsState(DEFAULT_SETTINGS);
    try {
      localStorage.setItem('access_tech_landpage_settings', JSON.stringify(DEFAULT_SETTINGS));
      window.dispatchEvent(new Event('landpage_settings_changed'));
    } catch (e) {
      console.error('Error saving defaults to localStorage', e);
    }
  };

  useEffect(() => {
    const handleSync = () => {
      try {
        const stored = localStorage.getItem('access_tech_landpage_settings');
        if (stored) {
          setSettingsState(JSON.parse(stored));
        }
      } catch (e) {
        console.error(e);
      }
    };

    window.addEventListener('landpage_settings_changed', handleSync);
    return () => {
      window.removeEventListener('landpage_settings_changed', handleSync);
    };
  }, []);

  return {
    settings,
    updateSettings,
    resetToDefault,
  };
}
